const cleanText = (value = "") => String(value || "").trim();

const socialPlatformDefinitions = {
  Instagram: {
    inputKind: "username",
    label: "Instagram username",
    placeholder: "yourusername",
    prefix: "@",
    build: (value) => `https://www.instagram.com/${encodeURIComponent(value)}/`
  },
  Facebook: {
    inputKind: "username",
    label: "Facebook username or page name",
    placeholder: "yourpagename",
    build: (value) => `https://www.facebook.com/${encodeURIComponent(value)}`
  },
  TikTok: {
    inputKind: "username",
    label: "TikTok username",
    placeholder: "yourusername",
    prefix: "@",
    build: (value) => `https://www.tiktok.com/@${encodeURIComponent(value)}`
  },
  YouTube: {
    inputKind: "username",
    label: "YouTube handle",
    placeholder: "yourchannel",
    prefix: "@",
    build: (value) => value.startsWith("UC")
      ? `https://www.youtube.com/channel/${encodeURIComponent(value)}`
      : `https://www.youtube.com/@${encodeURIComponent(value)}`
  },
  X: {
    inputKind: "username",
    label: "X username",
    placeholder: "yourusername",
    prefix: "@",
    build: (value) => `https://x.com/${encodeURIComponent(value)}`
  },
  Threads: {
    inputKind: "username",
    label: "Threads username",
    placeholder: "yourusername",
    prefix: "@",
    build: (value) => `https://www.threads.net/@${encodeURIComponent(value)}`
  },
  LinkedIn: {
    inputKind: "username",
    label: "LinkedIn public profile name",
    placeholder: "your-profile-name",
    build: (value) => `https://www.linkedin.com/in/${encodeURIComponent(value)}`
  },
  Snapchat: {
    inputKind: "username",
    label: "Snapchat username",
    placeholder: "yourusername",
    prefix: "@",
    build: (value) => `https://www.snapchat.com/add/${encodeURIComponent(value)}`
  },
  WhatsApp: {
    inputKind: "whatsapp",
    label: "Phone number or WhatsApp invite link",
    placeholder: "+61 400 000 000",
    build: (value) => {
      const digits = value.replace(/\D/g, "");
      return digits.length >= 7 && digits.length <= 15 ? `https://wa.me/${digits}` : "";
    }
  },
  Meetup: {
    inputKind: "username",
    label: "Meetup group name",
    placeholder: "your-badminton-group",
    build: (value) => `https://www.meetup.com/${encodeURIComponent(value)}/`
  },
  "Google Business": {
    inputKind: "url",
    label: "Google profile link",
    placeholder: "Paste your Google profile link"
  },
  "Booking page": {
    inputKind: "url",
    label: "Booking page link",
    placeholder: "Paste your booking page link"
  },
  Website: {
    inputKind: "url",
    label: "Website address",
    placeholder: "yourwebsite.com"
  },
  Other: {
    inputKind: "url",
    label: "Public profile link",
    placeholder: "Paste the public link"
  }
};

export const socialPlatformOptions = Object.keys(socialPlatformDefinitions);

export const mediaPlatformOptions = [
  "YouTube",
  "Instagram",
  "TikTok",
  "Facebook",
  "Vimeo",
  "Dailymotion",
  "Image",
  "Other"
];

const mediaPlatformDefinitions = {
  YouTube: { label: "YouTube video or Short", placeholder: "Paste a YouTube share link or video ID" },
  Instagram: { label: "Instagram reel or post", placeholder: "Paste an Instagram reel or post link" },
  TikTok: { label: "TikTok video", placeholder: "Paste a TikTok share link" },
  Facebook: { label: "Facebook video", placeholder: "Paste a public Facebook video link" },
  Vimeo: { label: "Vimeo video", placeholder: "Paste a Vimeo link or video ID" },
  Dailymotion: { label: "Dailymotion video", placeholder: "Paste a Dailymotion link or video ID" },
  Image: { label: "Public image", placeholder: "Paste a direct image link" },
  Other: { label: "Public media", placeholder: "Paste a public media or video link" }
};

const urlLikeSocialInput = (value = "") => {
  const input = cleanText(value);
  return /^(?:https?:)?\/\//i.test(input)
    || /^www\./i.test(input)
    || /^(?:instagram\.com|facebook\.com|fb\.com|tiktok\.com|youtube\.com|youtu\.be|x\.com|twitter\.com|threads\.net|linkedin\.com|snapchat\.com|wa\.me|chat\.whatsapp\.com|meetup\.com)\//i.test(input);
};

const socialPlatformHosts = {
  Instagram: ["instagram.com"],
  Facebook: ["facebook.com", "fb.com"],
  TikTok: ["tiktok.com"],
  YouTube: ["youtube.com"],
  X: ["x.com", "twitter.com"],
  Threads: ["threads.net"],
  LinkedIn: ["linkedin.com"],
  Snapchat: ["snapchat.com"],
  WhatsApp: ["wa.me", "whatsapp.com"],
  Meetup: ["meetup.com"]
};

const socialUrlMatchesPlatform = (platform, url) => {
  const allowedHosts = socialPlatformHosts[platform];
  if (!allowedHosts?.length) return true;
  try {
    const host = new URL(url).hostname.replace(/^www\./, "").toLowerCase();
    return allowedHosts.some((allowedHost) => host === allowedHost || host.endsWith(`.${allowedHost}`));
  } catch {
    return false;
  }
};

export const normalisePublicHttpUrl = (value = "") => {
  const input = cleanText(value);
  if (!input || /[\u0000-\u001f]/.test(input)) return "";
  const candidate = input.startsWith("//")
    ? `https:${input}`
    : /^(?:https?):\/\//i.test(input)
      ? input
      : `https://${input}`;
  try {
    const parsed = new URL(candidate);
    if (!["http:", "https:"].includes(parsed.protocol) || !parsed.hostname) return "";
    const publicHostname = parsed.hostname.includes(".")
      || /^\d{1,3}(?:\.\d{1,3}){3}$/.test(parsed.hostname)
      || parsed.hostname.includes(":");
    if (!publicHostname) return "";
    parsed.username = "";
    parsed.password = "";
    return parsed.href;
  } catch {
    return "";
  }
};

export const getSocialPlatformMeta = (platform = "Instagram") => (
  socialPlatformDefinitions[platform] || socialPlatformDefinitions.Other
);

const stripUsernameDecorators = (value = "") => cleanText(value)
  .replace(/^@+/, "")
  .replace(/^\/+|\/+$/g, "");

export const buildSocialProfileUrl = (platform = "Instagram", value = "") => {
  const definition = getSocialPlatformMeta(platform);
  const input = cleanText(value);
  if (!input) return "";

  if (definition.inputKind === "url") {
    return normalisePublicHttpUrl(input);
  }

  if (urlLikeSocialInput(input)) {
    const url = normalisePublicHttpUrl(input);
    return url && socialUrlMatchesPlatform(platform, url) ? url : "";
  }

  if (definition.inputKind === "whatsapp") {
    return definition.build(input);
  }

  const username = stripUsernameDecorators(input);
  if (!username || /\s/.test(username)) return "";
  return definition.build(username);
};

const parsedSocialUrl = (url = "") => {
  const normalised = normalisePublicHttpUrl(url);
  if (!normalised) return null;
  try {
    return new URL(normalised);
  } catch {
    return null;
  }
};

export const socialInputFromLink = (link = {}) => {
  if (cleanText(link.inputValue)) return cleanText(link.inputValue);
  if (cleanText(link.username)) return cleanText(link.username);
  const platform = cleanText(link.platform) || "Instagram";
  const url = cleanText(link.url);
  const definition = getSocialPlatformMeta(platform);
  if (!url || definition.inputKind === "url") return url;
  const parsed = parsedSocialUrl(url);
  if (!parsed) return url;
  const host = parsed.hostname.replace(/^www\./, "").toLowerCase();
  const parts = parsed.pathname.split("/").filter(Boolean).map((part) => decodeURIComponent(part));

  if (platform === "Instagram" && host.endsWith("instagram.com") && parts[0]) return parts[0].replace(/^@/, "");
  if (platform === "TikTok" && host.endsWith("tiktok.com") && parts[0]?.startsWith("@")) return parts[0].slice(1);
  if (platform === "YouTube" && host.endsWith("youtube.com")) {
    if (parts[0]?.startsWith("@")) return parts[0].slice(1);
    if (parts[0] === "channel" && parts[1]) return parts[1];
  }
  if (platform === "Facebook" && (host.endsWith("facebook.com") || host === "fb.com") && parts.length === 1) return parts[0];
  if (platform === "X" && (host === "x.com" || host.endsWith("twitter.com")) && parts.length === 1) return parts[0].replace(/^@/, "");
  if (platform === "Threads" && host.endsWith("threads.net") && parts[0]) return parts[0].replace(/^@/, "");
  if (platform === "LinkedIn" && host.endsWith("linkedin.com") && parts[0] === "in" && parts[1]) return parts[1];
  if (platform === "Snapchat" && host.endsWith("snapchat.com") && parts[0] === "add" && parts[1]) return parts[1];
  if (platform === "Meetup" && host.endsWith("meetup.com") && parts.length === 1) return parts[0];
  if (platform === "WhatsApp" && host === "wa.me" && parts[0]) return `+${parts[0].replace(/\D/g, "")}`;
  return url;
};

export const socialProfileInputError = (platform = "Instagram", value = "") => {
  const input = cleanText(value);
  if (!input) return "";
  const definition = getSocialPlatformMeta(platform);
  const url = buildSocialProfileUrl(platform, input);
  if (!url) {
    if (definition.inputKind === "url") return "Enter a valid public website or profile link.";
    if (definition.inputKind === "whatsapp") return "Enter a full international phone number or WhatsApp invite link.";
    return `Enter the ${platform} username without spaces, or paste a matching ${platform} profile link.`;
  }
  return "";
};

export const detectMediaPlatform = (value = "") => {
  const normalised = normalisePublicHttpUrl(value);
  if (!normalised) return "";
  try {
    const parsed = new URL(normalised);
    const host = parsed.hostname.replace(/^www\./, "").toLowerCase();
    if (host === "youtu.be" || host.endsWith("youtube.com")) return "YouTube";
    if (host.endsWith("instagram.com")) return "Instagram";
    if (host.endsWith("tiktok.com")) return "TikTok";
    if (host === "fb.watch" || host.endsWith("facebook.com")) return "Facebook";
    if (host.endsWith("vimeo.com")) return "Vimeo";
    if (host === "dai.ly" || host.endsWith("dailymotion.com")) return "Dailymotion";
    if (/\.(?:png|jpe?g|webp|gif|avif)(?:$|[?#])/i.test(normalised)) return "Image";
    return "Other";
  } catch {
    return "";
  }
};

export const getMediaPlatformMeta = (platform = "YouTube") => (
  mediaPlatformDefinitions[platform] || mediaPlatformDefinitions.Other
);

const youtubeVideoId = (input = "") => {
  const value = cleanText(input);
  if (/^[A-Za-z0-9_-]{11}$/.test(value)) return value;
  const normalised = normalisePublicHttpUrl(value);
  if (!normalised) return "";
  try {
    const parsed = new URL(normalised);
    const host = parsed.hostname.replace(/^www\./, "").toLowerCase();
    if (host === "youtu.be") return parsed.pathname.split("/").filter(Boolean)[0] || "";
    if (host.endsWith("youtube.com")) {
      return parsed.searchParams.get("v") || parsed.pathname.match(/\/(?:embed|shorts|live)\/([^/?#]+)/)?.[1] || "";
    }
  } catch {
    return "";
  }
  return "";
};

export const normaliseMediaLinkUrl = (platform = "YouTube", value = "") => {
  const input = cleanText(value);
  if (!input) return "";

  if (platform === "YouTube") {
    const id = youtubeVideoId(input);
    if (id) return `https://www.youtube.com/watch?v=${encodeURIComponent(id)}`;
  }
  if (platform === "Vimeo" && /^\d{5,15}$/.test(input)) return `https://vimeo.com/${input}`;
  if (platform === "Dailymotion" && /^[A-Za-z0-9]{5,12}$/.test(input)) return `https://www.dailymotion.com/video/${input}`;
  return normalisePublicHttpUrl(input);
};

export const mediaLinkInputError = (platform = "YouTube", value = "") => {
  const input = cleanText(value);
  if (!input) return "";
  const url = normaliseMediaLinkUrl(platform, input);
  if (!url) return "Paste a valid public share link.";

  let parsed;
  try {
    parsed = new URL(url);
  } catch {
    return "Paste a valid public share link.";
  }
  const host = parsed.hostname.replace(/^www\./, "").toLowerCase();
  const path = parsed.pathname;
  if (platform === "YouTube" && !youtubeVideoId(input)) return "Paste a YouTube video or Short, not a channel page.";
  if (platform === "Instagram" && !host.endsWith("instagram.com")) return "Paste an Instagram reel or post link.";
  if (platform === "Instagram" && !/\/(?:p|reel|tv)\/[^/?#]+/i.test(path)) return "Paste an Instagram reel or post, not a profile link.";
  if (platform === "TikTok") {
    const shortShareLink = ["vm.tiktok.com", "vt.tiktok.com"].includes(host) || /^\/t\//i.test(path);
    if (!host.endsWith("tiktok.com") || (!shortShareLink && !/\/video\/\d+/i.test(path))) return "Paste a public TikTok video or share link.";
  }
  if (platform === "Vimeo" && (!host.endsWith("vimeo.com") || !/\d+/.test(path))) return "Paste a Vimeo video link or video ID.";
  if (platform === "Dailymotion" && !(host === "dai.ly" || host.endsWith("dailymotion.com"))) return "Paste a Dailymotion video link or video ID.";
  if (platform === "Image") {
    const recognisedImageUrl = /\.(?:png|jpe?g|webp|gif|avif)(?:$|[?#])/i.test(url)
      || /(?:images?\.|cloudinary|fbcdn|cdninstagram|\/image\/|\/images\/)/i.test(url);
    if (!recognisedImageUrl) return "Paste a direct image link from a public website or image host.";
  }
  return "";
};

export const mediaLabelForPlatform = (platform = "YouTube") => {
  if (platform === "Image") return "Profile image";
  if (platform === "Other") return "Profile media";
  return `${platform} video`;
};
